import Axios, { AxiosResponse } from "axios";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { PaginatedResponseType } from "../../globals/types";
import {
    IMockup,
    IGetMockupsRequest,
    mockupsAtom,
    mockupsLoadingAtom,
    hasMoreMockupsAtom,
    singleMockupAtom,
} from "./types";
import { buildQueryStringFromParams } from "../../components/helpers";

type SetMockupType = (payload: PaginatedResponseType<IMockup>) => void;
type FetchMockupsType = (query: IGetMockupsRequest, lazy?: boolean) => void;
type DeleteMockupType = (mockup_id: number) => Promise<boolean>;
type AddMockupType = (form: FormData) => Promise<IMockup | null>;
type UpdateMockupType = (id: number, form: FormData) => Promise<IMockup | null>;
type GetMockupByIdType = (id: number) => Promise<IMockup | null>;

type MockupKeyFunctions = {
    get: FetchMockupsType;
    delete: DeleteMockupType;
    add: AddMockupType;
    update: UpdateMockupType;
    getById: GetMockupByIdType;
}

type UseMockups = [
    PaginatedResponseType<IMockup>,
    SetMockupType,
    MockupKeyFunctions
];

export const useMockups = (): UseMockups => {
    const [mockups, setMockups] = useAtom(mockupsAtom);
    const setHasMoreMockups = useSetAtom(hasMoreMockupsAtom);
    const setSingleMockup = useSetAtom(singleMockupAtom);
    const mockupsRef = useRef(mockups);

    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();
    const setMockupsLoading = useSetAtom(mockupsLoadingAtom);
    const isFetchingRef = useRef(false);
    const lastQueryKeyRef = useRef<string | null>(null);

    useEffect(() => {
        mockupsRef.current = mockups;
    }, [mockups]);

    const getMockups = useCallback(async (query: IGetMockupsRequest, lazy = false) => {
        if (!authToken || isFetchingRef.current) return;
        const queryKey = `${lazy ? "lazy" : "page"}:${buildQueryStringFromParams(query)}`;
        if (lastQueryKeyRef.current === queryKey) return;
        lastQueryKeyRef.current = queryKey;
        isFetchingRef.current = true;
        setMockupsLoading(true);
        try {
            const mockupsResults = await Axios.get(
                `${environment.mockupsUrl}${buildQueryStringFromParams(query)}`,
                {
                    headers: {
                        "X-Wallmates-Auth": authToken
                    }
                }
            );
            if (mockupsResults?.data) {
                const mockupsData = lazy
                    ? {
                        ...mockupsResults.data,
                        results: [
                            ...mockupsRef.current.results,
                            ...mockupsResults.data.results.filter((mockup: IMockup) => {
                                const foundExisting = mockupsRef.current.results.find(
                                    (existingMockup: IMockup) => existingMockup.id === mockup.id
                                );
                                if (foundExisting) {
                                    return false;
                                }
                                return true;
                            })
                        ]
                    }
                    : mockupsResults.data;
                if (!mockupsData.hasNext) {
                    setHasMoreMockups(false);
                }
                setMockups(mockupsData);
                return mockupsResults.data;
            }
            lastQueryKeyRef.current = null;
            setSnackbar({
                show: true,
                snackbarLevel: "error",
                text: "There was an error while getting mockups. Please try again later."
            });
        } catch (err: any) {
            lastQueryKeyRef.current = null;
            errorCatcher(err, loginFunctions.logout);
        } finally {
            isFetchingRef.current = false;
            setMockupsLoading(false);
        }
    }, [authToken, loginFunctions.logout, setHasMoreMockups, setMockups, setMockupsLoading, setSnackbar]);

    const add = useCallback(async (form: FormData): Promise<IMockup | null> => {
        const insertResponse: AxiosResponse<IMockup> | void = await Axios.post(environment.mockupsUrl, form, {
            headers: {
                'X-Wallmates-Auth': authToken,
                "Content-Type": "multipart/form-data"
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (insertResponse) {
            const nextMockups = {
                ...mockupsRef.current,
                results: [...mockupsRef.current.results, insertResponse.data]
            };
            setMockups(nextMockups);
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Mockup added successfully.'
            });
            // Fire-and-forget analysis request for Patternly similarity pipeline
            // Try endpoint /mockups/:id/analyze, fallback to /mockups/analyze { mockup_id }
            (async () => {
                try {
                    const createdId = insertResponse.data.id as number | undefined;
                    if (!createdId) return;
                    // Prefer RESTful analyze-by-id endpoint if available
                    await Axios.post(`${environment.mockupsUrl}/${createdId}/analyze`, {}, {
                        headers: {
                            'X-Wallmates-Auth': authToken
                        }
                    }).catch(async () => {
                        // Fallback legacy shape
                        await Axios.post(`${environment.mockupsUrl}/analyze`, { mockup_id: createdId }, {
                            headers: {
                                'X-Wallmates-Auth': authToken
                            }
                        }).catch(() => { /* swallow error - non-blocking */ });
                    });
                } catch {
                    // no-op; analysis is best-effort and should not block UX
                }
            })();
            return insertResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while adding a new mockup. Please try again later.'
            })
            return null;
        }
    }, [authToken, loginFunctions.logout, setMockups, setSnackbar]);

    const deleteMock = useCallback(async (mockupId: number): Promise<boolean> => {
        const deleteResponse: AxiosResponse<boolean> | void = await Axios.delete(`${environment.mockupsUrl}?mockup_id=${mockupId}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (deleteResponse) {
            const resetMockups = {
                ...mockupsRef.current,
                results: mockupsRef.current.results.filter((item: IMockup) => item.id !== mockupId)
            };
            setMockups(resetMockups);
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Mockup deleted successfully.'
            })
            return deleteResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while deleting a mockup. Please try again later.'
            })
            return false;
        }
    }, [authToken, loginFunctions.logout, setMockups, setSnackbar]);

    const updateMockup = useCallback(async (id: number, body: FormData): Promise<IMockup | null> => {
        const updateResponse: AxiosResponse<IMockup> | void = await Axios.put(`${environment.mockupsUrl}`, body, {
            headers: {
                'X-Wallmates-Auth': authToken,
                "Content-Type": "multipart/form-data"
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (updateResponse) {
            const resetMockups = {
                ...mockupsRef.current,
                results: [
                    ...mockupsRef.current.results.map((mockup: IMockup) => {
                        if (mockup.id === id) {
                            return updateResponse.data;
                        }
                        return mockup;
                    })
                ]
            };
            setMockups(resetMockups);
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Mockup updated successfully.'
            })
            return updateResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while updating a mockup. Please try again later.'
            })
            return null;
        }
    }, [authToken, loginFunctions.logout, setMockups, setSnackbar]);

    const getById = useCallback(async (id: number): Promise<IMockup | null> => {
        const getByIdResponse: AxiosResponse<IMockup> | void = await Axios.get(`${environment.mockupsUrl}/${id}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (getByIdResponse) {
            setSingleMockup(getByIdResponse.data);
            return getByIdResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while getting a mockup. Please try again later.'
            })
            return null;
        }
    }, [authToken, loginFunctions.logout, setSingleMockup, setSnackbar]);

    const keyFunctions: MockupKeyFunctions = useMemo(() => ({
        add: add,
        delete: deleteMock,
        get: getMockups,
        update: updateMockup,
        getById: getById
    }), [add, deleteMock, getById, getMockups, updateMockup]);

    return [
        mockups,
        setMockups,
        keyFunctions
    ]
}

